import download from "downloadjs";
import moment from "moment";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";

const THEME = {
    titleBar: "#302b63",
    border: "#1a1a1a",
    metaText: "#111111",
    groupHeader: {
        info: "#8e86aa",
        sales: "#8e86aa",
        converted: "#f59e0b",
        cancel: "#7a7298",
        reject: "#7a7298",
        pending: "#4a90c2",
        convertedPct: "#f59e0b",
    },
    subHeader: {
        info: "#b5adc8",
        sales: "#b5adc8",
        converted: "#ffcc66",
        cancel: "#c8c0d8",
        reject: "#b8b0c8",
        pending: "#8cb4dc",
        convertedPct: "#ffcc66",
    },
    dataTint: {
        converted: "#fff2cc",
        pending: "#dceaf7",
        convertedPct: "#fff2cc",
    },
    summary: {
        base: "#302b63",
        converted: "#e67300",
        pending: "#0052cc",
        convertedPct: "#e67300",
    },
    rowWhite: "#ffffff",
    rowGrey: "#e6e6e6",
};

const COLUMN_DEFS = [
    { key: "no", label: "NO.", weight: 1, section: "info", align: "center" },
    { key: "project", label: "PROJECT NAME", weight: 4.5, section: "info", align: "left" },
    {
        key: "allCases",
        label: "UNITS",
        weight: 1.2,
        section: "sales",
        align: "center",
        format: "unit",
    },
    {
        key: "totalGrossGdv",
        label: "GROSS GDV",
        weight: 2.75,
        section: "sales",
        align: "center",
        format: "currency",
    },
    {
        key: "totalNetGdv",
        label: "NET GDV",
        weight: 2.75,
        section: "sales",
        align: "center",
        format: "currency",
    },
    {
        key: "convertedCases",
        label: "UNITS",
        weight: 1.2,
        section: "converted",
        align: "center",
        format: "unit",
    },
    {
        key: "convertGrossGdv",
        label: "GROSS GDV",
        weight: 2.75,
        section: "converted",
        align: "center",
        format: "currency",
    },
    {
        key: "convertNetGdv",
        label: "NET GDV",
        weight: 2.75,
        section: "converted",
        align: "center",
        format: "currency",
    },
    {
        key: "cancelledCases",
        label: "UNITS",
        weight: 1.2,
        section: "cancel",
        align: "center",
        format: "unit",
    },
    {
        key: "cancelledGrossGdv",
        label: "GROSS GDV",
        weight: 2.75,
        section: "cancel",
        align: "center",
        format: "currency",
    },
    {
        key: "cancelledNetGdv",
        label: "NET GDV",
        weight: 2.75,
        section: "cancel",
        align: "center",
        format: "currency",
    },
    {
        key: "rejectedCases",
        label: "UNITS",
        weight: 1.2,
        section: "reject",
        align: "center",
        format: "unit",
    },
    {
        key: "rejectedGrossGdv",
        label: "GROSS GDV",
        weight: 2.75,
        section: "reject",
        align: "center",
        format: "currency",
    },
    {
        key: "rejectedNetGdv",
        label: "LOAN REJECT",
        weight: 2.75,
        section: "reject",
        align: "center",
        format: "currency",
    },
    {
        key: "pendingCases",
        label: "SALES",
        weight: 1.2,
        section: "pending",
        align: "center",
        format: "unit",
    },
    {
        key: "pendingGrossGdv",
        label: "GROSS GDV",
        weight: 2.75,
        section: "pending",
        align: "center",
        format: "currency",
    },
    {
        key: "pendingNetGdv",
        label: "NET GDV",
        weight: 2.75,
        section: "pending",
        align: "center",
        format: "currency",
    },
    {
        key: "conversionRate",
        label: "CONVERTED + SIGNEDLO PERCENTAGE",
        weight: 2,
        section: "convertedPct",
        align: "center",
        format: "percent",
    },
];

const GROUP_HEADERS = [
    { label: "", start: 0, end: 1, section: "info" },
    { label: "SALES", start: 2, end: 4, section: "sales" },
    { label: "CONVERTED + SIGNEDLO", start: 5, end: 7, section: "converted" },
    { label: "CANCEL", start: 8, end: 10, section: "cancel" },
    { label: "LOAN REJECT", start: 11, end: 13, section: "reject" },
    { label: "PENDING", start: 14, end: 16, section: "pending" },
    {
        label: "CONVERTED + SIGNEDLO PERCENTAGE",
        start: 17,
        end: 17,
        section: "convertedPct",
    },
];

const SECTION_BOUNDARIES = [1, 4, 7, 10, 13, 16];

const hexToRgb = (hex) => {
    const normalized = hex.replace("#", "");
    return rgb(
        parseInt(normalized.slice(0, 2), 16) / 255,
        parseInt(normalized.slice(2, 4), 16) / 255,
        parseInt(normalized.slice(4, 6), 16) / 255
    );
};

const sanitizePdfText = (value) => {
    if (value === null || value === undefined) {
        return "";
    }

    return String(value)
        .replace(/\u2014/g, "-")
        .replace(/\u2013/g, "-")
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/[^\u0000-\u00FF]/g, "");
};

const buildScaledColumns = (tableWidth, defs = COLUMN_DEFS) => {
    const totalWeight = defs.reduce((sum, col) => sum + col.weight, 0);
    return defs.map((col) => ({
        ...col,
        width: (col.weight / totalWeight) * tableWidth,
    }));
};

const getDataCellBg = (section, rowIndex, isTotalRow) => {
    if (isTotalRow) {
        if (section === "converted" || section === "convertedPct") {
            return THEME.summary.converted;
        }
        if (section === "pending") {
            return THEME.summary.pending;
        }
        return THEME.summary.base;
    }

    if (THEME.dataTint[section]) {
        return THEME.dataTint[section];
    }

    return rowIndex >= 10 ? THEME.rowGrey : THEME.rowWhite;
};

const exportPerformancePdf = async ({
    rows,
    totals,
    dateFrom,
    dateTo,
    directorLabel,
    directorType,
    nameField = "project",
    nameHeader = "PROJECT NAME",
    reportSubtitle = "PD Project Performance",
    filePrefix = "Performance-Report",
}) => {
    try {
        const numberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        const formatValue = (value, format) => {
            if (format === "currency") {
                if (value === null || value === undefined) {
                    return "-";
                }
                const numericValue =
                    typeof value === "number" ? value : parseFloat(value);
                if (Number.isNaN(numericValue)) {
                    return "-";
                }
                return `RM${numberFormatter.format(numericValue)}`;
            }

            if (format === "percent") {
                const numericValue =
                    typeof value === "number" ? value : parseFloat(value);
                return `${numberFormatter.format(
                    Number.isNaN(numericValue) ? 0 : numericValue
                )}%`;
            }

            if (format === "unit") {
                return String(value || 0);
            }

            return String(value ?? "");
        };

        const pdfDoc = await PDFDocument.create();
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

        const pageWidth = 842;
        const pageHeight = 595;
        const marginX = 10;
        const marginTop = 24;
        const marginBottom = 28;
        const tableWidth = pageWidth - marginX * 2;
        const tableX = marginX;
        const columnDefs = COLUMN_DEFS.map((col) =>
            col.key === "project" ? { ...col, label: nameHeader } : col
        );
        const COLUMNS = buildScaledColumns(tableWidth, columnDefs);

        const cellPaddingX = 4;
        const cellPaddingY = 3;
        const metaRowHeight = 14;
        const metaGap = 8;
        const titleBarHeight = 20;
        const groupHeaderHeight = 16;
        const subHeaderRowHeight = 18;
        const dataRowHeight = 14;
        const borderColor = hexToRgb(THEME.border);

        let page = pdfDoc.addPage([pageWidth, pageHeight]);
        let cursorY = pageHeight - marginTop;

        const getColumnX = (index) => {
            let x = tableX;
            for (let i = 0; i < index; i += 1) {
                x += COLUMNS[i].width;
            }
            return x;
        };

        const getGroupWidth = (start, end) => {
            let width = 0;
            for (let i = start; i <= end; i += 1) {
                width += COLUMNS[i].width;
            }
            return width;
        };

        const drawSectionDividers = (topY, bottomY) => {
            SECTION_BOUNDARIES.forEach((colIndex) => {
                const x = getColumnX(colIndex + 1);
                page.drawLine({
                    start: { x, y: topY },
                    end: { x, y: bottomY },
                    thickness: 1.2,
                    color: borderColor,
                });
            });
        };

        const drawCellText = ({
            text,
            x,
            y,
            width,
            height,
            align = "left",
            size = 5,
            bold = false,
            color = hexToRgb(THEME.metaText),
        }) => {
            const activeFont = bold ? fontBold : font;
            let displayText = sanitizePdfText(text);

            if (!displayText) {
                return;
            }

            const maxWidth = width - cellPaddingX * 2;
            while (
                displayText.length > 1 &&
                activeFont.widthOfTextAtSize(displayText, size) > maxWidth
            ) {
                displayText = displayText.slice(0, -1);
            }

            if (displayText !== sanitizePdfText(text) && displayText.length > 3) {
                displayText = `${displayText.slice(0, -3)}...`;
            }

            let textX = x + cellPaddingX;
            const textWidth = activeFont.widthOfTextAtSize(displayText, size);

            if (align === "center") {
                textX = x + (width - textWidth) / 2;
            } else if (align === "right") {
                textX = x + width - textWidth - cellPaddingX;
            }

            page.drawText(displayText, {
                x: textX,
                y: y - height + cellPaddingY + (height - cellPaddingY * 2 - size) / 2,
                size,
                font: activeFont,
                color,
            });
        };

        const drawRowCells = ({
            y,
            height,
            rowData,
            rowIndex,
            mode = "data",
        }) => {
            const isSubHeader = mode === "subHeader";
            const isTotalRow = mode === "total";

            COLUMNS.forEach((column, index) => {
                const x = getColumnX(index);
                let bgHex;
                let textColor = hexToRgb(THEME.metaText);

                if (isSubHeader) {
                    bgHex = THEME.subHeader[column.section];
                } else if (isTotalRow) {
                    bgHex = getDataCellBg(column.section, rowIndex, true);
                    textColor = rgb(1, 1, 1);
                } else {
                    bgHex = getDataCellBg(column.section, rowIndex, false);
                }

                page.drawRectangle({
                    x,
                    y: y - height,
                    width: column.width,
                    height,
                    color: hexToRgb(bgHex),
                    borderColor,
                    borderWidth: 0.35,
                });

                let text = "";
                if (isSubHeader) {
                    text = column.label;
                } else if (column.key === "no") {
                    text = isTotalRow ? "" : String(rowIndex + 1);
                } else if (column.key === "project") {
                    text = isTotalRow
                        ? "SUMMARY TOTAL"
                        : rowData[nameField] || rowData.project || "";
                } else {
                    text = formatValue(rowData[column.key], column.format);
                }

                drawCellText({
                    text,
                    x,
                    y,
                    width: column.width,
                    height,
                    align: column.align,
                    size: isSubHeader ? 5.5 : 5.5,
                    bold: isSubHeader || isTotalRow,
                    color: textColor,
                });
            });
        };

        const drawGroupHeaderRow = (y, directorName = "") => {
            GROUP_HEADERS.forEach((group) => {
                const x = getColumnX(group.start);
                const width = getGroupWidth(group.start, group.end);
                const sectionKey = group.section;

                page.drawRectangle({
                    x,
                    y: y - groupHeaderHeight,
                    width,
                    height: groupHeaderHeight,
                    color: hexToRgb(THEME.groupHeader[sectionKey]),
                    borderColor,
                    borderWidth: 0.35,
                });

                const label =
                    group.start === 0 && group.end === 1 && directorName
                        ? directorName.toUpperCase()
                        : group.label;

                if (label) {
                    drawCellText({
                        text: label,
                        x,
                        y,
                        width,
                        height: groupHeaderHeight,
                        align: "center",
                        size: 6,
                        bold: true,
                        color: rgb(1, 1, 1),
                    });
                }
            });
        };

        const drawTableHeaders = (y, directorName) => {
            drawGroupHeaderRow(y, directorName);
            const subHeaderY = y - groupHeaderHeight;
            drawRowCells({
                y: subHeaderY,
                height: subHeaderRowHeight,
                mode: "subHeader",
            });
            return subHeaderY - subHeaderRowHeight;
        };

        let tableTopY = cursorY;

        const formattedFrom = moment(dateFrom).format("DD.MM.YYYY");
        const formattedTo = moment(dateTo).format("DD.MM.YYYY");
        const monthLabel = moment(dateFrom).format("MMMM").toUpperCase();
        const directorSuffix = directorLabel
            ? ` (${directorType || "DIRECTOR"} - ${directorLabel.toUpperCase()})`
            : "";

        const metaPaddingX = 0;

        drawCellText({
            text: `${monthLabel} - D&D OVERALL SALES & CONVERSION REPORT`,
            x: tableX + metaPaddingX,
            y: cursorY,
            width: tableWidth * 0.55 - metaPaddingX,
            height: metaRowHeight,
            size: 7,
            bold: true,
        });

        drawCellText({
            text: `${reportSubtitle} ${formattedFrom} - ${formattedTo}`,
            x: tableX + tableWidth * 0.45,
            y: cursorY,
            width: tableWidth * 0.55 - metaPaddingX,
            height: metaRowHeight,
            align: "right",
            size: 6.5,
            bold: true,
        });

        cursorY -= metaRowHeight + metaGap;

        const mainTitle = sanitizePdfText(
            `D&D ' KL HQ - ANNUAL SALES & CONVERSION RATE${directorSuffix}`
        );

        page.drawRectangle({
            x: tableX,
            y: cursorY - titleBarHeight,
            width: tableWidth,
            height: titleBarHeight,
            color: hexToRgb(THEME.titleBar),
            borderColor,
            borderWidth: 0.5,
        });

        drawCellText({
            text: mainTitle,
            x: tableX,
            y: cursorY,
            width: tableWidth,
            height: titleBarHeight,
            align: "center",
            size: 8,
            bold: true,
            color: rgb(1, 1, 1),
        });

        cursorY -= titleBarHeight;
        tableTopY = cursorY;
        cursorY = drawTableHeaders(cursorY, directorLabel);
        let tableBottomY = cursorY;

        const ensureSpace = (neededHeight) => {
            if (cursorY - neededHeight < marginBottom) {
                drawSectionDividers(tableTopY, tableBottomY);
                page.drawRectangle({
                    x: tableX,
                    y: tableBottomY,
                    width: tableWidth,
                    height: tableTopY - tableBottomY,
                    borderColor,
                    borderWidth: 0.6,
                });

                page = pdfDoc.addPage([pageWidth, pageHeight]);
                cursorY = pageHeight - marginTop;
                tableTopY = cursorY;
                cursorY = drawTableHeaders(cursorY, directorLabel);
                tableBottomY = cursorY;
            }
        };

        rows.forEach((row, index) => {
            ensureSpace(dataRowHeight);
            drawRowCells({
                y: cursorY,
                height: dataRowHeight,
                rowData: row,
                rowIndex: index,
                mode: "data",
            });
            cursorY -= dataRowHeight;
            tableBottomY = cursorY;
        });

        if (totals) {
            ensureSpace(dataRowHeight);
            drawRowCells({
                y: cursorY,
                height: dataRowHeight,
                rowData: totals,
                rowIndex: rows.length,
                mode: "total",
            });
            cursorY -= dataRowHeight;
            tableBottomY = cursorY;
        }

        drawSectionDividers(tableTopY, tableBottomY);
        page.drawRectangle({
            x: tableX,
            y: tableBottomY,
            width: tableWidth,
            height: tableTopY - tableBottomY,
            borderColor,
            borderWidth: 0.6,
        });

        const pdfBytes = await pdfDoc.save();
        download(
            pdfBytes,
            `${filePrefix}-${moment(dateFrom).format("YYYY-MM-DD")}-${moment(dateTo).format("YYYY-MM-DD")}.pdf`,
            "application/pdf"
        );
    } catch (error) {
        console.error("Error generating performance PDF:", error);
        alert("Error generating PDF. Please try again.");
    }
};

export default exportPerformancePdf;
